import { IconButton, TextArea } from '@civilgrid/ui/components';
import { DateTime } from 'luxon';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { EditorMode, type Project } from '~/common/types';
import { Dropdown } from '~/components2/Dropdown';
import Icon from '~/components2/Icon';
import { commentExtraSpacing, getCircleBgColor } from '~/components2/Map/canvas/comment/common';
import { editCommentObject } from '~/components2/Map/canvas/comment/EditCommentObject';
import { getTimeDifference } from '~/helpers/timeDifference';
import rootStore from '~/store';
import { useCivilDrawStore } from '../civilDrawStoreContext';

export default function Comments() {
  const store = useCivilDrawStore();
  const { me } = useSnapshot(rootStore);
  const { currentObject, currentMapId, project } = useSnapshot(store) as typeof store;
  const { commentObject } = useSnapshot(editCommentObject) as typeof editCommentObject;
  const isInitialClick = useRef(true);
  const textareaRef = useRef<HTMLInputElement>(null);
  const [currentComment, setCurrentComment] = useState<string>();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { current: map } = useMap();
  const currentUser = me;

  const handleClickAway = (event: MouseEvent) => {
    if (event.type === 'click' && textareaRef.current) {
      if (isInitialClick.current) {
        isInitialClick.current = false;
        return;
      }

      if (event.target instanceof HTMLElement && !textareaRef.current.contains(event.target)) {
        isInitialClick.current = true; // reset initial click back to true
        setCurrentComment('');
      }
    }
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && store.editorMode === EditorMode.EditComment) {
      store.setEditorMode(EditorMode.None);
      store.currentObjectId = undefined;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    document.addEventListener('click', handleClickAway);
    document.addEventListener('keyup', handleEscape);

    return () => {
      document.removeEventListener('click', handleClickAway);
      document.removeEventListener('keyup', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (currentComment?.trim().length === 0 || !currentComment) {
      setIsButtonDisabled(true);
    } else {
      setIsButtonDisabled(false);
    }
  }, [currentComment]);

  if (!map || !currentObject || currentObject.type !== 'comment') return null;

  const lngLat = currentObject?.geometry.coordinates as [number, number];
  const coordinates = map.project(lngLat);

  const onCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentComment(e.target.value);
  };

  const onCommentSubmit = async () => {
    await store.addComment(currentComment!);
    setCurrentComment('');
    const analyticsProject = project as unknown as Project;
    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawCommentAdded, {
      project_id: analyticsProject.id,
      organization_id: analyticsProject.organization || undefined,
      ...(currentMapId && { map_id: currentMapId }),
    });
  };

  const onCommentArchive = async (id: string, deleteCopy: string) => {
    store.archiveComment(id);

    const analyticsProject = project as unknown as Project;
    if (deleteCopy.includes('group')) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectDeleted, {
        project_id: analyticsProject.id,
        organization_id: analyticsProject.organization || undefined,
        object_type: mapObjectTypeToCivildrawObjectType(currentObject.type),
        ...(currentMapId && { map_id: currentMapId }),
        civildraw_use_new_drawing_canvas: false,
      });
    } else {
      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawCommentDeleted, {
        project_id: analyticsProject.id,
        organization_id: analyticsProject.organization || undefined,
        ...(currentMapId && { map_id: currentMapId }),
      });
    }
  };

  if (commentObject?.isDragging) return null;

  return (
    <div
      className={`absolute w-96 rounded bg-white text-2xl text-primary shadow-lg`}
      style={{
        top: coordinates.y,
        left: coordinates.x + commentExtraSpacing + 30,
      }}
      ref={textareaRef}
    >
      {currentObject?.commentGroup.length === 0 ? (
        <div className="flex items-center gap-2 p-4">
          <TextArea
            value={currentComment ?? ''}
            onChange={(e) => onCommentChange(e)}
            name="addComment"
            id="addComment"
            placeholder="Add a comment..."
            aria-label="Add a comment"
            className="max-h-20 resize-none"
            data-testid="civildraw-comment-input"
            autoFocus
          />
          <IconButton
            variant="secondary"
            icon="envelopeClosed"
            onClick={onCommentSubmit}
            aria-label="Send comment"
            data-testid="civildraw-comment-submit"
          />
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="max-h-52 overflow-scroll">
            {currentObject?.commentGroup.map((o, idx) => {
              if (o.status === 'archived') return null;

              // @ts-expect-error - Argument of type 'DateTime' is not assignable to parameter of type 'string'.
              const formattedDate = DateTime.fromISO(o.updatedAt, {
                outputCalendar: 'unix',
              });

              // is current user the creator of the comment?
              const isCurrentUser = currentUser?.email === o.createdBy.email;

              const initial = o.createdBy.name.charAt(0).toUpperCase();

              const deleteCopy =
                idx === 0 && currentObject?.commentGroup.length >= 1 ? 'Delete comment group' : 'Delete comment';

              return (
                <div key={o.id}>
                  {/* existing comments */}
                  <div className="flex items-start justify-between p-4">
                    <div className="flex gap-2">
                      <div
                        className={`flex h-6 w-6 items-center justify-center rounded-full ${getCircleBgColor({
                          isComponentRequest: true,
                          initial,
                        })}`}
                      >
                        <span className="text-xs font-bold text-white ">{o.createdBy.name ? initial : ''}</span>
                      </div>
                      <div className="flex flex-1 flex-col gap-1">
                        <span className="text-xs font-bold">{o.createdBy.name}</span>
                        <span className="text-xs text-grey-50">{`Edited: ${getTimeDifference(
                          formattedDate,
                        )} ago`}</span>
                        <span className="whitespace-pre text-xs">{o.comment}</span>
                      </div>
                    </div>
                    <Dropdown
                      align="end"
                      sideOffset={18}
                      alignOffset={-18}
                      triggerElement={
                        <button
                          type="button"
                          aria-label={`Comment actions for ${o.createdBy.name}`}
                          data-testid="civildraw-comment-menu-trigger"
                        >
                          <Icon icon="moreHorizontal" size={18} className="cursor-pointer" />
                        </button>
                      }
                      items={[
                        {
                          type: 'simple',
                          label: deleteCopy,
                          onClick: () => onCommentArchive(o.id, deleteCopy),
                          isDisabled: !isCurrentUser,
                        },
                      ]}
                    />
                  </div>
                  {idx !== currentObject?.commentGroup.length - 1 && <hr className="m-0 w-full border-grey-20" />}
                </div>
              );
            })}
          </div>
          {/* add comment */}
          <hr className="m-0 w-full border-grey-20" />
          <div className="flex items-center gap-2 p-4">
            <TextArea
              value={currentComment ?? ''}
              onChange={(e) => onCommentChange(e)}
              name="replyComment"
              id="replyComment"
              placeholder="Reply..."
              aria-label="Reply to comment"
              className="max-h-20 resize-none"
              data-testid="civildraw-comment-reply-input"
            />
            <IconButton
              icon="envelopeClosed"
              disabled={isButtonDisabled}
              onClick={onCommentSubmit}
              aria-label="Send reply"
              data-testid="civildraw-comment-reply-submit"
            />
          </div>
        </div>
      )}
    </div>
  );
}
